/**
 * Signatures: capture on the office screen (e.g. the customer at the counter), and the
 * Manager's "Customer not present" with a reason. Signatures are append-only; the latest counts.
 */
import { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { SIGNATURE_FORM_LABELS, type Me, type SignatureRole } from '@wo/shared';
import { api, errorText } from '../../lib/api';
import { dateTime } from '../../lib/format';
import type { Sig, SignaturesView } from '../../lib/form-types';
import { Button, Card, ErrorText, Field, Input, Loading, Modal, Textarea } from '../../components/ui';
import { SignatureBox, type SignatureHandle } from '../../components/SignatureBox';

export function SigImage({ label, sig }: { label: string; sig: Sig | null }) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase text-slate-500">{label}</div>
      {sig ? (
        <>
          <img src={`/api/files/${sig.fileId}`} alt={`${label} — ${sig.signerName}`} className="h-24 w-full rounded border bg-white object-contain" />
          <div className="text-xs text-slate-600">{sig.signerName} · {dateTime(sig.signedAt)}{sig.offline && ' · offline'}</div>
        </>
      ) : (
        <div className="flex h-24 items-center justify-center rounded border border-dashed text-sm text-slate-400">Not signed</div>
      )}
    </div>
  );
}

export function SignaturesTab({ woId, me, readOnly, onChange }: { woId: number; me: Me; readOnly: boolean; onChange: () => void }) {
  const q = useQuery({ queryKey: ['wo-signatures', woId], queryFn: () => api.get<SignaturesView>(`/api/work-orders/${woId}/signatures`) });
  const [capture, setCapture] = useState<SignatureRole | null>(null);
  const [notPresent, setNotPresent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (q.isLoading || !q.data) return <Loading />;
  const s = q.data;
  const isManager = me.role === 'ADMIN' || me.role === 'MANAGER';
  const refresh = () => {
    void q.refetch();
    onChange();
  };

  return (
    <div className="space-y-4">
      <ErrorText error={error} />
      <Card title={`Signatures — ${SIGNATURE_FORM_LABELS[s.signatureForm]}`}>
        {(s.signatureForm === 'FIELD_REPORT' || s.signatureForm === 'LUBE') && <p className="mb-3 text-xs leading-relaxed text-slate-600">{s.certification.text}</p>}
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <SigImage label="Customer signature" sig={s.latest.customer} />
            {s.customerNotPresentReason && <p className="mt-1 rounded bg-amber-50 p-2 text-sm">Customer not present: “{s.customerNotPresentReason}”</p>}
            {!readOnly && (
              <div className="mt-2 flex flex-wrap gap-2">
                <Button variant="secondary" onClick={() => setCapture('CUSTOMER')}>Customer signs here</Button>
                {isManager && <Button variant="secondary" onClick={() => setNotPresent(true)}>{s.customerNotPresentReason ? 'Change / clear “Customer not present”' : 'Customer not present'}</Button>}
              </div>
            )}
          </div>
          <div>
            <SigImage label="Technician signature" sig={s.latest.technician} />
            {!readOnly && isManager && <Button className="mt-2" variant="secondary" onClick={() => setCapture('TECHNICIAN')}>Technician signs here</Button>}
          </div>
        </div>
      </Card>
      <Card title="All signatures (kept, newest counts)">
        {s.history.length === 0 && <p className="text-sm text-slate-500">None yet.</p>}
        <ul className="space-y-1 text-sm">
          {s.history.map((h) => (
            <li key={h.id}>
              {SIGNATURE_FORM_LABELS[h.form]} · {h.role.toLowerCase()} · {h.signerName} · {dateTime(h.signedAt)}
              {h.statementVersion && <span className="text-slate-500"> · statement v{h.statementVersion}</span>}
              {h.offline && <span className="ml-1 rounded bg-slate-200 px-1 text-xs">offline</span>}
              <a className="ml-2 text-blue-700 underline" href={`/api/files/${h.fileId}`} target="_blank" rel="noreferrer">view</a>
            </li>
          ))}
        </ul>
      </Card>
      {capture && (
        <CaptureDialog
          role={capture}
          onClose={() => setCapture(null)}
          onSave={async (signerName, image) => {
            try {
              await api.post(`/api/work-orders/${woId}/signatures`, { form: s.signatureForm, role: capture, signerName, image });
              setCapture(null);
              refresh();
            } catch (e) {
              setError(errorText(e));
            }
          }}
        />
      )}
      {notPresent && (
        <NotPresentDialog
          current={s.customerNotPresentReason}
          onClose={() => setNotPresent(false)}
          onSave={async (reason) => {
            try {
              await api.post(`/api/work-orders/${woId}/customer-not-present`, { reason });
              setNotPresent(false);
              refresh();
            } catch (e) {
              setError(errorText(e));
            }
          }}
        />
      )}
    </div>
  );
}

function CaptureDialog({ role, onClose, onSave }: { role: SignatureRole; onClose: () => void; onSave: (name: string, image: string) => void }) {
  const pad = useRef<SignatureHandle>(null);
  const [name, setName] = useState('');
  const [empty, setEmpty] = useState(true);
  return (
    <Modal
      title={role === 'CUSTOMER' ? 'Customer signature' : 'Technician signature'}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={() => pad.current?.clear()}>Clear</Button>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button disabled={empty || name.trim().length < 2} onClick={() => onSave(name.trim(), pad.current!.toDataURL())}>Save signature</Button>
        </>
      }
    >
      <SignatureBox ref={pad} height={180} onChange={setEmpty} />
      <Field label="Printed name" required><Input value={name} onChange={(e) => setName(e.target.value)} /></Field>
    </Modal>
  );
}

function NotPresentDialog({ current, onClose, onSave }: { current: string | null; onClose: () => void; onSave: (reason: string | null) => void }) {
  const [reason, setReason] = useState(current ?? '');
  return (
    <Modal
      title="Customer not present"
      onClose={onClose}
      footer={
        <>
          {current && <Button variant="secondary" onClick={() => onSave(null)}>Clear it</Button>}
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button disabled={reason.trim().length < 10} onClick={() => onSave(reason.trim())}>Save</Button>
        </>
      }
    >
      <p className="text-sm text-slate-600">Closes without a customer signature. Logged, and shown on the weekly integrity report.</p>
      <Field label="Why couldn't the customer sign? (at least 10 characters)" required><Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} /></Field>
    </Modal>
  );
}
